// ============================================================================
// src/pages/public/LoginPage.jsx
// -----------------------------------------------------------------------------
// Page de connexion Beta Corelia
// Objectif : sas d’entrée simple avant accès à l’application
// ============================================================================

import React, { useEffect, useState } from "react";
import "../../styles/main.css";
import theme from "../../styles/theme";

import PageHeader from "../../components/reusable/ModuleHeader/PageHeader";
import Button from "../../components/reusable/Forms/Button";
import TextField from "../../components/reusable/Forms/TextField";
import InfoPanel from "../../components/reusable/Forms/InfoPanel";

// ================================================================
// Composant principal : LoginPage
// ================================================================
const LoginPage = () => {
  // --------------------------------------------------------------
  // State formulaire
  // --------------------------------------------------------------
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // --------------------------------------------------------------
  // State UI
  // --------------------------------------------------------------
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");

  // --------------------------------------------------------------
  // Redirection si déjà connecté
  // --------------------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("corelia_token");
    if (token) {
      window.location.href = "/HomePage";
    }
  }, []);

  // --------------------------------------------------------------
  // Gestion des champs
  // --------------------------------------------------------------
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // --------------------------------------------------------------
  // Soumission
  // --------------------------------------------------------------
  const handleLogin = async () => {
    try {
      setLoading(true);
      setError("");
      setInfoMessage("");

      if (!formData.email || !formData.password) {
        throw new Error("Veuillez renseigner l’email et le mot de passe.");
      }

      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
        }),
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Email ou mot de passe invalide.");
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      localStorage.setItem("corelia_token", data.token);
      localStorage.setItem("corelia_user", JSON.stringify(data.user));

      window.location.href = "/HomePage";
    } catch (err) {
      setError(err.message || "Connexion impossible.");
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------
  // Soumission avec Enter
  // --------------------------------------------------------------
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  // --------------------------------------------------------------
  // Rendu
  // --------------------------------------------------------------
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: theme.colors.background.app,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 760,
          background: theme.colors.background.form,
          borderRadius: theme.borders.radius.md,
          padding: 32,
          boxShadow: theme.shadows.sm,
          border: `1px solid ${theme.colors.borders.panel}`,
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <PageHeader
            logo={{ src: "/assets/logos/corelia.png", label: "Quantum Corelia" }}
            title=""
            subtitle="Accès à l’environnement Corelia"
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing.lg,
          }}
        >
          <TextField
            label="Email"
            value={formData.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="Entrez votre email"
            onKeyDown={handleKeyDown}
          />

          <TextField
            label="Mot de passe"
            value={formData.password}
            onChange={(e) => handleChange("password", e.target.value)}
            placeholder="Entrez votre mot de passe"
            type="password"
            onKeyDown={handleKeyDown}
          />

          {error && (
            <InfoPanel
              title="Connexion impossible"
              message={error}
              variant="red"
              stripPosition="left"
              border={true}
              rounded={true}
            />
          )}

          {infoMessage && (
            <InfoPanel
              title="Information"
              message={infoMessage}
              variant="blue"
              stripPosition="left"
              border={true}
              rounded={true}
            />
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              gap: theme.spacing.md,
              marginTop: 4,
            }}
          >
            <Button variant="blue" onClick={handleLogin} disabled={loading}>
              {loading ? "Connexion..." : "Se connecter"}
            </Button>
          </div>

          <div style={{ marginTop: 8 }}>
            <InfoPanel
              title="Phase Beta"
              message="La création de compte n’est pas encore ouverte. L’accès se fait uniquement avec un utilisateur déjà enregistré."
              variant="yellow"
              stripPosition="left"
              border={true}
              rounded={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;